/**
 * @file authedFetch.ts
 * @description fetch() wrapper for the few endpoints called outside RTK Query.
 *
 * Mirrors baseQueryWithReauth: attaches the current access token, and on a 401
 * silently refreshes via the HTTP-only refresh cookie and retries once. Without
 * this, a stale token in localStorage fails forever with no recovery path.
 */
import type { PublicUserProfile } from '@forex-builder/shared-types';

/** Reads the access token. The legacy 'token' key is retired in authSlice. */
export const readAccessToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('accessToken');
};

export interface AuthedFetchResult {
  res: Response;
  /** Present when a silent refresh succeeded, so callers can update the store. */
  refreshed?: { user: PublicUserProfile; accessToken: string };
}

export const authedFetch = async (
  url: string,
  init: RequestInit = {},
): Promise<AuthedFetchResult> => {
  const call = (token: string | null): Promise<Response> =>
    fetch(url, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(init.headers ?? {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

  const res = await call(readAccessToken());
  if (res.status !== 401) return { res };

  // Access token missing, expired, or left over from an older build — the
  // refresh cookie is the source of truth, so try it before giving up.
  const refreshRes = await fetch('/api/v1/auth/refresh', { method: 'POST' });
  if (!refreshRes.ok) return { res };

  const json = (await refreshRes.json()) as {
    success?: boolean;
    data?: { user?: PublicUserProfile; accessToken?: string };
  };
  const accessToken = json.data?.accessToken;
  const user = json.data?.user;
  if (!json.success || !accessToken || !user) return { res };

  localStorage.setItem('accessToken', accessToken);
  localStorage.removeItem('token'); // retire the legacy key so it cannot resurface

  return { res: await call(accessToken), refreshed: { user, accessToken } };
};
